import { Injectable } from '@angular/core';
import { AppState } from './../../app.service';
import { AppVars } from './../../app.vars';

/**
 * Configuration interface for enhanced autocomplete behavior
 */
export interface AutocompleteConfig {
  /** Number of items to show in initial subset (default: 10) */
  initialSubsetSize: number;
  /** Minimum characters required to trigger full search (default: 3) */
  searchThreshold: number;
  /** Whether to sort the initial subset alphabetically (default: true) */
  sortInitialSubset: boolean;
  /** Minimum number of items to switch from dropdown to autocomplete (default: 15) */
  autocompleteThreshold: number;
}

/**
 * Result interface for autocomplete filtering
 */
export interface AutocompleteFilterResult<T> {
  filteredItems: T[];
  showingInitialSubset: boolean;
}

/**
 * Enhanced Autocomplete Service
 *
 * Provides consistent autocomplete behavior with initial subset display
 * and remote configuration capabilities.
 */
@Injectable({
  providedIn: 'root',
})
export class EnhancedAutocompleteService {
  private defaultConfig: AutocompleteConfig = {
    initialSubsetSize: 10,
    searchThreshold: 3,
    sortInitialSubset: true,
    autocompleteThreshold: 15,
  };

  constructor(private appState: AppState) {}

  /**
   * Merges default, remote (from AppState), and per-call configurations.
   */
  private getFinalConfig(config?: Partial<AutocompleteConfig>): AutocompleteConfig {
    const orgMetadata = this.appState.get(AppVars.ORG_METADATA);
    const remoteConfig = orgMetadata ? orgMetadata['auto-complete-config'] : null;
    return { ...this.defaultConfig, ...remoteConfig, ...config };
  }

  /**
   * Filters items with enhanced autocomplete logic.
   */
  filterItems<T>(
    items: T[],
    searchTerm: string,
    displayFn: (item: T) => string,
    config?: Partial<AutocompleteConfig>
  ): AutocompleteFilterResult<T> {
    const finalConfig = this.getFinalConfig(config);

    if (!searchTerm || searchTerm.length < finalConfig.searchThreshold) {
      return {
        filteredItems: this.getInitialSubset(items, displayFn, finalConfig),
        showingInitialSubset: true,
      };
    } else {
      return {
        filteredItems: this.getFilteredResults(items, searchTerm, displayFn),
        showingInitialSubset: false,
      };
    }
  }

  /**
   * Initialize subset for autocomplete focus/open events.
   */
  initializeSubset<T>(items: T[], displayFn: (item: T) => string, config?: Partial<AutocompleteConfig>): AutocompleteFilterResult<T> {
    const finalConfig = this.getFinalConfig(config);
    return {
      filteredItems: this.getInitialSubset(items, displayFn, finalConfig),
      showingInitialSubset: true,
    };
  }

  /**
   * Check if autocomplete should be used based on item count.
   */
  shouldUseAutocomplete<T>(items: T[], config?: Partial<AutocompleteConfig>): boolean {
    const finalConfig = this.getFinalConfig(config);
    return items && items.length >= finalConfig.autocompleteThreshold;
  }

  /**
   * Get the total count message for hint display.
   */
  getHintMessage<T>(items: T[], itemType: string, config?: Partial<AutocompleteConfig>): string {
    const finalConfig = this.getFinalConfig(config);
    if (!items || items.length === 0) {
      return '';
    }
    return `Type ${finalConfig.searchThreshold}+ characters to search all ${items.length} ${itemType}...`;
  }

  /**
   * Private method to get initial subset of items.
   */
  private getInitialSubset<T>(items: T[], displayFn: (item: T) => string, config: AutocompleteConfig): T[] {
    if (!items) {
      return [];
    }
    let subset = [...items];
    if (config.sortInitialSubset) {
      subset.sort((a, b) => displayFn(a).localeCompare(displayFn(b)));
    }
    return subset.slice(0, config.initialSubsetSize);
  }

  /**
   * Private method to get filtered search results.
   */
  private getFilteredResults<T>(items: T[], searchTerm: string, displayFn: (item: T) => string): T[] {
    if (!items) {
      return [];
    }
    const searchLower = searchTerm.toLowerCase();
    return items
      .filter((item) => displayFn(item).toLowerCase().includes(searchLower))
      .sort((a, b) => displayFn(a).localeCompare(displayFn(b)));
  }

  /**
   * Utility method for simple string array filtering.
   */
  filterStringItems(items: string[], searchTerm: string, config?: Partial<AutocompleteConfig>): AutocompleteFilterResult<string> {
    return this.filterItems(items, searchTerm, (item) => item, config);
  }

  /**
   * Initialize subset for string arrays.
   */
  initializeStringSubset(items: string[], config?: Partial<AutocompleteConfig>): AutocompleteFilterResult<string> {
    return this.initializeSubset(items, (item) => item, config);
  }
}
